import { UnitSystem } from '@trackman/baseball-shared-components';
import { ReactComponent as DefaultStrikeZoneImage } from 'components/images/defaultStrikeZoneImage.svg';
import { ReactComponent as TopBottomStrikeZoneImage } from 'components/images/topBottomStrikeZoneImage.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { LeagueStrikeZoneEnum } from 'interfaces/session/IStrikeZoneDefinition';
import { IApiLeagueStrikeZones } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { unitConverters } from 'services/unitConverters';
import { LineupStore } from 'stores/LineupStore';
import { StrikeZoneExplanation, StrikeZoneValue, StrikeZoneWrapper } from './SelectStrikeZoneStyles';
import HeightBased from './StrikeZoneExplanations/HeightBased';
import Static from './StrikeZoneExplanations/Static';
import TopBottom from './StrikeZoneExplanations/TopBottom';

const defaultPosition = { top: 0, left: 0, height: 0, width: 0 };

interface IProps {
  selectedStrikeZone?: keyof IApiLeagueStrikeZones;
  isPrivateGame: boolean;
}

const StrikeZoneImage: React.FC<IProps> = observer((props) => {
  const { selectedStrikeZone, isPrivateGame } = props;
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const szWidth = useRef<SVGElement | null>(null);
  const szHeight = useRef<SVGElement | null>(null);
  const szInnerHeight = useRef<SVGElement | null>(null);
  const szOuterHeight = useRef<SVGElement | null>(null);
  const middleHeight = useRef<SVGElement | null>(null);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const widthRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const topBottomRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const middleRef = useRef<HTMLDivElement | null>(null);

  const [widthPositions, setWidthPositions] = useState(defaultPosition);
  const [topPositions, setTopPositions] = useState(defaultPosition);
  const [topBottomPositions, setTopBottomPositions] = useState(defaultPosition);
  const [bottomPositions, setBottomPositions] = useState(defaultPosition);

  const calculatePosition = (
    target: React.MutableRefObject<SVGElement | null>,
    parent: React.MutableRefObject<HTMLDivElement | null>,
    cellRef: React.MutableRefObject<HTMLDivElement | null>
  ): typeof defaultPosition => {
    if (target.current && parent.current && cellRef.current) {
      const { top, left, height } = target.current.getBoundingClientRect();
      const { width } = cellRef.current.getBoundingClientRect();
      const parentTop = parent.current.getBoundingClientRect().top;
      const parentLeft = parent.current.getBoundingClientRect().left;

      return {
        top: top - parentTop,
        left: left - parentLeft - width / 4.5,
        width: width,
        height: height,
      };
    }

    return defaultPosition;
  };

  const getExplanationContent = (): JSX.Element => {
    if (selectedStrikeZone === LeagueStrikeZoneEnum.TopAndBottomStrikeZone) {
      return <TopBottom />;
    }
    if (selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone && lineupStore.league.strikeZones?.heightBasedStrikeZone) {
      return <HeightBased heightBasedStrikeZone={lineupStore.league?.strikeZones.heightBasedStrikeZone} />;
    }
    if (selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone) {
      return <Static />;
    }

    return <div>This is based on the chosen league.</div>;
  };

  useEffect(() => {
    if (wrapper && wrapper.current) {
      szWidth.current = wrapper.current.querySelector('#strikezone-width') as SVGElement;
      szHeight.current = wrapper.current.querySelector('#strikezone-height') as SVGElement;
      szInnerHeight.current = wrapper.current.querySelector('#strikezone-inner-height') as SVGElement;
      szOuterHeight.current = wrapper.current.querySelector('#strikezone-outer-height') as SVGElement;
      middleHeight.current = wrapper.current.querySelector('#strikezone-middle') as SVGElement;

      setWidthPositions(calculatePosition(szWidth, wrapper, widthRef));
      setTopPositions(calculatePosition(szHeight, wrapper, topRef));
      setTopBottomPositions(calculatePosition(szInnerHeight, wrapper, topBottomRef));
      setBottomPositions(calculatePosition(szOuterHeight, wrapper, bottomRef));
    }
  }, [lineupStore.metrics, selectedStrikeZone]);

  const getValueFromPercentage = (value: number, metrics: UnitSystem) => {
    const PLACEHOLDER_PLAYER_HEIGHT = 72; // inches

    const estimatedValue = (unitConverters.getValue(PLACEHOLDER_PLAYER_HEIGHT, metrics) as any) * (value / 100);

    return unitConverters.getMetricValue(estimatedValue, metrics);
  };

  const getWidthValue = () => {
    switch (selectedStrikeZone) {
      case LeagueStrikeZoneEnum.StaticStrikeZone:
        return lineupStore.overridedStrikeZone?.staticStrikeZoneWidth;
      case LeagueStrikeZoneEnum.HeightBasedStrikeZone:
        return lineupStore.overridedStrikeZone?.heightStrikeZoneWidth;
      case LeagueStrikeZoneEnum.TopAndBottomStrikeZone:
        return lineupStore.overridedStrikeZone?.topBottomStrikeZoneWidth;
    }
  };
  const getBottomValue = () => {
    switch (selectedStrikeZone) {
      case LeagueStrikeZoneEnum.StaticStrikeZone:
        return lineupStore.overridedStrikeZone?.staticStrikeZoneBottom;
      case LeagueStrikeZoneEnum.HeightBasedStrikeZone:
        return lineupStore.overridedStrikeZone?.heightStrikeZoneBottomPercentage;
    }
  };
  const getTopValue = () => {
    switch (selectedStrikeZone) {
      case LeagueStrikeZoneEnum.StaticStrikeZone:
        return lineupStore.overridedStrikeZone?.staticStrikeZoneTop;
      case LeagueStrikeZoneEnum.HeightBasedStrikeZone:
        return lineupStore.overridedStrikeZone?.heightStrikeZoneTopPercentage;
    }
  };
  const getTopBottomValue = () => {
    switch (selectedStrikeZone) {
      case LeagueStrikeZoneEnum.StaticStrikeZone:
        return (lineupStore.overridedStrikeZone?.staticStrikeZoneTop || 0) - (lineupStore.overridedStrikeZone?.staticStrikeZoneBottom || 0);
      case LeagueStrikeZoneEnum.HeightBasedStrikeZone:
        return (
          (lineupStore.overridedStrikeZone?.heightStrikeZoneTopPercentage || 0) -
          (lineupStore.overridedStrikeZone?.heightStrikeZoneBottomPercentage || 0)
        );
    }
  };

  return (
    <StrikeZoneWrapper ref={(ref) => (wrapper.current = ref)} isPrivateGame={isPrivateGame} variant={selectedStrikeZone}>
      {(selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone ||
        selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone) && <DefaultStrikeZoneImage data-testid='strikezone-image' />}
      {selectedStrikeZone === LeagueStrikeZoneEnum.TopAndBottomStrikeZone && <TopBottomStrikeZoneImage data-testid='strikezone-image' />}

      <StrikeZoneValue
        ref={widthRef}
        top={widthPositions.top}
        left={widthPositions.left}
        width={widthPositions.width}
        height={widthPositions.height}
        data-testid='strikezone-width'
      >
        {unitConverters.getMetricValue(getWidthValue() || 0, lineupStore.metrics)}
      </StrikeZoneValue>
      {(selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone ||
        selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone) && (
        <>
          <StrikeZoneValue
            ref={bottomRef}
            top={topPositions.top}
            left={topPositions.left}
            width={topPositions.width}
            height={topPositions.height}
            data-testid='strikezone-bottom'
            variant={selectedStrikeZone}
          >
            {selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone &&
              unitConverters.getMetricValue(getBottomValue() || 0, lineupStore.metrics)}
            {selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone &&
              getValueFromPercentage(getBottomValue() || 0, lineupStore.metrics)}
          </StrikeZoneValue>
          <StrikeZoneValue
            ref={topBottomRef}
            top={topBottomPositions.top}
            left={topBottomPositions.left}
            width={topBottomPositions.width}
            height={topBottomPositions.height}
            data-testid='strikezone-top-bottom'
            variant={selectedStrikeZone}
          >
            {selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone &&
              unitConverters.getMetricValue(getTopBottomValue() || 0, lineupStore.metrics)}
            {selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone &&
              getValueFromPercentage(getTopBottomValue() || 0, lineupStore.metrics)}
          </StrikeZoneValue>
          <StrikeZoneValue
            ref={topRef}
            top={bottomPositions.top}
            left={bottomPositions.left}
            width={bottomPositions.width}
            height={bottomPositions.height}
            data-testid='strikezone-top'
            variant={selectedStrikeZone}
          >
            {selectedStrikeZone === LeagueStrikeZoneEnum.StaticStrikeZone &&
              unitConverters.getMetricValue(getTopValue() || 0, lineupStore.metrics)}
            {selectedStrikeZone === LeagueStrikeZoneEnum.HeightBasedStrikeZone &&
              getValueFromPercentage(getTopValue() || 0, lineupStore.metrics)}
          </StrikeZoneValue>
        </>
      )}
      <StrikeZoneExplanation ref={middleRef} isPrivateGame={isPrivateGame}>
        {getExplanationContent()}
      </StrikeZoneExplanation>
    </StrikeZoneWrapper>
  );
});

export default StrikeZoneImage;
